import React, { useState } from 'react';
import { useGetCustomersQuery, useExportCustomersCSVMutation } from '../../services/api';
import { formatCurrency } from '../../utils/helpers';
import ProtectedRoute from '../../components/ProtectedRoute/ProtectedRoute';
import Loading from '../../components/Loading/Loading';
import Error from '../../components/Error/Error';
import './Customers.css';

const Customers = () => {
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: ''
  });

  const { data, isLoading, error, refetch } = useGetCustomersQuery(filters);
  const [exportCSV] = useExportCustomersCSVMutation();

  const customers = data?.data?.customers || [];
  const pagination = data?.data?.pagination;

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  const handleExportCSV = async () => {
    try {
      const blob = await exportCSV().unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'customers.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to export CSV:', error);
    }
  };

  if (isLoading) {
    return <Loading text="Loading customers..." />;
  }

  if (error) {
    return <Error message="Failed to load customers" onRetry={refetch} />;
  }

  return (
    <ProtectedRoute roles={['OWNER']}>
      <div className="container">
        <div className="page-header">
          <h1>Customers</h1>
          <div className="header-actions">
            <button onClick={handleExportCSV} className="btn btn-secondary">
              Export CSV
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSearch} className="filters-form">
              <div className="filters-grid">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Search by name, email, or mobile..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="form-control"
                  />
                </div>
                <button type="submit" className="btn btn-primary">
                  Search
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Customers Table */}
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Total Orders</th>
                  <th>Total Spent</th>
                  <th>First Order</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer._id}>
                    <td>
                      <strong>{customer.name}</strong>
                    </td>
                    <td>{customer.email}</td>
                    <td>{customer.mobile}</td>
                    <td>
                      <span className="badge">{customer.totalOrders}</span>
                    </td>
                    <td>{formatCurrency(customer.totalSpent)}</td>
                    <td>{new Date(customer.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
                {customers.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center text-muted">
                      No customers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={filters.page === 1}
              className="btn btn-secondary"
            >
              Previous
            </button>
            
            <span className="pagination-info">
              Page {pagination.page} of {pagination.pages}
            </span>
            
            <button
              onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={filters.page === pagination.pages}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </ProtectedRoute>
  );
};

export default Customers;